import { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Loader2, Send } from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { EmptyState, TableWrap, th, td, fmtDate } from "@/components/dashboard/ui";

const inputCls =
  "w-full bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:ring-2 focus:ring-blue-500/50 placeholder:text-slate-500";
const labelCls = "text-[11px] uppercase tracking-widest text-slate-500 mb-1 block";

const STATUS_META = {
  open:        { label: "Open",         cls: "bg-amber-500/15 text-amber-300 border-amber-500/30" },
  in_progress: { label: "In progress",  cls: "bg-blue-500/15 text-blue-300 border-blue-500/30" },
  resolved:    { label: "Resolved",     cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  closed:      { label: "Closed",       cls: "bg-white/5 text-slate-400 border-white/10" },
};

export default function AdminTickets({ onChange }) {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [active, setActive] = useState(null);
  const [form, setForm] = useState({ status: "in_progress", priority: "medium", admin_reply: "" });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/tickets");
      setTickets(data);
    } catch (e) {
      toast.error("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const rows = filter === "all" ? tickets : tickets.filter((t) => t.status === filter);
  const counts = tickets.reduce((acc, t) => ({ ...acc, [t.status]: (acc[t.status] || 0) + 1 }), {});

  const openTicket = (t) => {
    setActive(t);
    setForm({ status: t.status, priority: t.priority || "medium", admin_reply: t.admin_reply || "" });
  };

  const saveTicket = async (e) => {
    e.preventDefault();
    if (!active) return;
    setSaving(true);
    try {
      await api.patch(`/admin/tickets/${active.ticket_id}`, form);
      toast.success("Ticket updated");
      setActive(null);
      load();
      onChange && onChange();
    } catch (err) {
      toast.error(formatApiError(err, "Update failed"));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="admin-tickets">
      <div>
        <h2 className="font-display text-2xl tracking-tight text-white">Support Tickets</h2>
        <p className="mt-1.5 text-slate-400">Reply to artist and label requests. Owners are emailed automatically.</p>
      </div>

      <div className="flex items-center gap-2 border-b border-white/10 overflow-x-auto">
        {[
          { key: "all", label: `All (${tickets.length})` },
          { key: "open", label: `Open ${counts.open ? `(${counts.open})` : ""}` },
          { key: "in_progress", label: `In progress ${counts.in_progress ? `(${counts.in_progress})` : ""}` },
          { key: "resolved", label: `Resolved ${counts.resolved ? `(${counts.resolved})` : ""}` },
          { key: "closed", label: `Closed ${counts.closed ? `(${counts.closed})` : ""}` },
        ].map((f) => (
          <button
            key={f.key}
            data-testid={`ticket-filter-${f.key}`}
            onClick={() => setFilter(f.key)}
            className={`px-4 py-2.5 text-sm border-b-2 transition-colors -mb-px whitespace-nowrap ${
              filter === f.key ? "border-white text-white" : "border-transparent text-slate-400 hover:text-white"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="py-20 flex justify-center"><Loader2 className="animate-spin text-slate-500" /></div>
      ) : rows.length === 0 ? (
        <EmptyState title="No tickets in this view" desc="Artists and labels open tickets from their Support tab." />
      ) : (
        <TableWrap>
          <thead>
            <tr>
              <th className={th}>Requester</th>
              <th className={th}>Category</th>
              <th className={th}>Subject</th>
              <th className={th}>Status</th>
              <th className={th}>Opened</th>
              <th className={th}></th>
            </tr>
          </thead>
          <tbody>
            {rows.map((t) => (
              <tr key={t.ticket_id} data-testid={`admin-ticket-row-${t.ticket_id}`} className="hover:bg-white/[0.02]">
                <td className={td}>
                  <div className="text-white">{t.user_display_name}</div>
                  <div className="text-xs text-slate-500">{t.user_email} · {t.user_role}</div>
                </td>
                <td className={td}>
                  <span className="text-xs text-slate-300">{t.category_label}</span>
                </td>
                <td className={td + " max-w-[380px]"}>
                  <div className="text-white truncate">{t.title}</div>
                  <div className="text-xs text-slate-500 truncate">{t.description}</div>
                </td>
                <td className={td}>
                  <span className={`inline-flex items-center text-[11px] font-medium px-2.5 py-1 rounded-full border ${(STATUS_META[t.status] || STATUS_META.open).cls}`}>
                    {(STATUS_META[t.status] || STATUS_META.open).label}
                  </span>
                </td>
                <td className={td}>{fmtDate(t.created_at)}</td>
                <td className={td + " text-right"}>
                  <button
                    data-testid={`ticket-open-${t.ticket_id}`}
                    onClick={() => openTicket(t)}
                    className="rounded-lg border border-white/10 hover:bg-white/10 text-slate-300 hover:text-white px-3 py-1.5 text-xs"
                  >
                    Reply
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      <Dialog open={!!active} onOpenChange={(v) => !v && setActive(null)}>
        <DialogContent className="bg-[#0b1220] border-white/10 text-white sm:max-w-lg" data-testid="admin-ticket-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{active?.title}</DialogTitle>
            <DialogDescription className="text-slate-400">
              {active?.category_label} · from {active?.user_display_name} ({active?.user_email})
            </DialogDescription>
          </DialogHeader>
          <div className="rounded-xl border border-white/10 bg-white/[0.02] p-3 text-sm text-slate-300 whitespace-pre-wrap max-h-[180px] overflow-y-auto">
            {active?.description}
          </div>
          <form onSubmit={saveTicket} className="space-y-3" data-testid="admin-ticket-form">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelCls}>Status</label>
                <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                  <SelectTrigger data-testid="ticket-status" className="bg-white/5 border-white/10 text-white h-[44px] rounded-xl text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className={labelCls}>Priority</label>
                <Select value={form.priority} onValueChange={(v) => setForm((f) => ({ ...f, priority: v }))}>
                  <SelectTrigger data-testid="ticket-priority" className="bg-white/5 border-white/10 text-white h-[44px] rounded-xl text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#0b1220] border-white/10 text-white">
                    <SelectItem value="low">Low</SelectItem>
                    <SelectItem value="medium">Medium</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <label className={labelCls}>Reply (emailed to the artist)</label>
              <textarea
                data-testid="ticket-reply"
                value={form.admin_reply}
                onChange={(e) => setForm((f) => ({ ...f, admin_reply: e.target.value }))}
                className={inputCls + " min-h-[140px]"}
                placeholder="Type your response here…"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setActive(null)} className="rounded-full border border-white/15 text-white text-sm px-5 py-2.5 hover:bg-white/5">Cancel</button>
              <button
                data-testid="admin-ticket-save"
                type="submit"
                disabled={saving}
                className="inline-flex items-center gap-2 rounded-full bg-white text-black font-medium text-sm px-6 py-2.5 hover:bg-slate-200 disabled:opacity-60"
              >
                {saving ? <Loader2 size={16} className="animate-spin" /> : <><Send size={14} /> Send reply</>}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
